import type { ReactNode } from "react";

import { cn } from "@/lib/utils";
import {
  inboundArrivalUnitKindTileLabel,
  parseInboundSubcontainerUnitKind,
} from "@/pages/shipping/inboundArrivalUnitKind";
import type { WarehouseRecountMpnContainerLine } from "@/lib/warehouseRecountApi";

const CONTAINER_HINT_MAX_LINES = 30;

function containerShelfLabel(line: WarehouseRecountMpnContainerLine): string {
  const trimmed = line.sublocation_label.trim();
  if (trimmed === "" || trimmed === "No sub location") {
    return "—";
  }
  return trimmed;
}

function containerUnitLabel(line: WarehouseRecountMpnContainerLine): string {
  return inboundArrivalUnitKindTileLabel(parseInboundSubcontainerUnitKind(line.unit_kind));
}

export function warehouseRecountMpnContainerHoverHint(input: {
  containers: readonly WarehouseRecountMpnContainerLine[];
  onSelectContainer?: (boxHex: string) => void;
}): ReactNode {
  if (input.containers.length === 0) {
    return "No labeled container details.";
  }
  const sorted = [...input.containers].sort((left, right) => {
    const shelfOrder = containerShelfLabel(left).localeCompare(containerShelfLabel(right), undefined, {
      numeric: true,
      sensitivity: "base",
    });
    if (shelfOrder !== 0) return shelfOrder;
    if (right.quantity !== left.quantity) return right.quantity - left.quantity;
    return left.short_code.localeCompare(right.short_code);
  });
  const visible = sorted.slice(0, CONTAINER_HINT_MAX_LINES);
  const hiddenCount = input.containers.length - visible.length;
  const selectable = input.onSelectContainer != null;
  const showShelf = visible.some((line) => containerShelfLabel(line) !== "—");

  return (
    <div className="text-left">
      <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Containers</p>
      <table className="w-max border-collapse text-xs text-slate-800">
        <thead>
          <tr className="border-b border-slate-200 text-[10px] font-medium uppercase tracking-wide text-slate-500">
            <th className="px-2 py-1 text-left font-medium">Carton</th>
            <th className="px-2 py-1 text-right font-medium">Qty</th>
            <th className="px-2 py-1 text-left font-medium">Type</th>
            {showShelf ? <th className="px-2 py-1 text-left font-medium">Shelf</th> : null}
          </tr>
        </thead>
        <tbody>
          {visible.map((line) => {
            const rowCells = (
              <>
                <td className="px-2 py-1 font-mono whitespace-nowrap">
                  {line.hide_carton_code || line.short_code.trim() === "" ? "—" : line.short_code}
                </td>
                <td className="px-2 py-1 text-right tabular-nums whitespace-nowrap">
                  {line.quantity.toLocaleString()}
                </td>
                <td className="px-2 py-1 whitespace-nowrap">
                  {line.hide_carton_code || line.unit_kind.trim() === "" ? "—" : containerUnitLabel(line)}
                </td>
                {showShelf ? (
                  <td className="px-2 py-1 font-mono whitespace-nowrap">{containerShelfLabel(line)}</td>
                ) : null}
              </>
            );

            if (!selectable) {
              return (
                <tr key={line.hex} className="border-t border-slate-100">
                  {rowCells}
                </tr>
              );
            }

            return (
              <tr
                key={line.hex}
                className={cn(
                  "border-t border-slate-100",
                  "cursor-pointer hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500",
                )}
                role="button"
                tabIndex={0}
                onClick={() => input.onSelectContainer?.(line.hex)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    input.onSelectContainer?.(line.hex);
                  }
                }}
              >
                {rowCells}
              </tr>
            );
          })}
        </tbody>
      </table>
      {hiddenCount > 0 ? (
        <p className="mt-1.5 text-xs text-slate-500">
          and {hiddenCount.toLocaleString()} more container{hiddenCount === 1 ? "" : "s"}
        </p>
      ) : null}
    </div>
  );
}
